import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  useCallback,
  lazy,
  Suspense
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useParams } from 'react-router-dom';
import { OpportunityService } from "../../services/OpportunityService";
import { useQuery } from "../../hooks/useQuery";
import { AuthContext } from "../../context/AuthContext";
import { useGetParams } from "../../hooks/useGetParams";
import { useMutations } from "../../hooks/useMutations";
import useAnalytics from '../../hooks/useAnalytics';
import SEOMetadata from "../SEOMetadata/SEOMetadata";
import SocialShareButtons from "../SocialShareButtons/SocialShareButtons";
import { FaGraduationCap, FaFilter, FaLinkedin, FaTwitter, FaFacebook, FaInstagram } from "react-icons/fa";
import OpportunityLayout from "./OpportunityLayout";
import OpportunityFilters from "./OpportunityFilters";
import WetsusBanner from '../WetsusBanner/WetsusBanner';

// Lazy load heavy components
const OpportunityGrid = lazy(() => import('../OpportunityGrid/OpportunityGrid'));

function replaceHyphensWithSpaces(str) {
  return str.replace(/-/g, ' ');
}

const encodeSpaces = (str) => {
  return str ? str.replace(/ /g, '%20') : '';
};

function replaceSpacesWithHyphens(str) {
  return str.replace(/\s+/g, '-');
}

function useMemoizedDate(date) {
  return useMemo(() => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }, [date]);
}

export const Opportunities = () => {
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID); // Initialize the analytics hook
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [showLast24HourOnly, setShowLast24HourOnly] = useState(false);
  const [finalSavedOpportunities, setSavedOpportunities] = useState(new Set());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showFullyFundedOnly, setShowFullyFundedOnly] = useState(false);
  const opportunitiesPerPage = 30;
  const sidebarRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { qsparams, setQueryParams } = useGetParams();
  const { userDetails: user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Extract parameters based on the URL structure
  const paramsFromUrl = useParams();
  const { category = "standard" } = paramsFromUrl;

  // Determine if the URL follows the new structure
  const isNewUrlStructure = location.pathname.includes("/discipline/");

  // Extract parameters dynamically based on the URL structure
  const { discipline, country, university } = isNewUrlStructure
    ? paramsFromUrl
    : { discipline: undefined, country: paramsFromUrl.country, university: paramsFromUrl.university };

  // Set effective values based on URL parameters
  const effectiveCategory = category || "standard";
  const effectiveCountry = replaceHyphensWithSpaces(country || "");
  const effectiveUniversity = replaceHyphensWithSpaces(university || "");
  const effectiveDiscipline = replaceHyphensWithSpaces(discipline || "");

  const getLast24HoursTimestamp = () => {
    const now = Math.floor(Date.now() / 1000);
    return now - (24 * 60 * 60);
  };

  const params = useMemo(() => {
    const opportunityParams = {
      category: category || 'standard',
      discipline: selectedDiscipline ? JSON.stringify({ in: [selectedDiscipline] }) : undefined,
      country: selectedCountry ? JSON.stringify({ in: [selectedCountry] }) : undefined,
      university: selectedUniversity ? JSON.stringify({ in: [selectedUniversity] }) : undefined,
      department: selectedDiscipline ? JSON.stringify({ in: [selectedDiscipline] }) : undefined,
      limit: opportunitiesPerPage, // Fixed at 30
      offset: (currentPage - 1) * opportunitiesPerPage, // Calculate offset
      'created_at[gt]': startDate ? Math.floor(startDate.getTime() / 1000) : undefined,
      'created_at[lt]': endDate ? Math.floor(endDate.getTime() / 1000) : undefined,
    };

    // Existing parameters for filters..
    if (showFullyFundedOnly) {
      opportunityParams['funded'] = true;
    }

    // Remove undefined values
    Object.keys(opportunityParams).forEach(key => opportunityParams[key] === undefined && delete opportunityParams[key]);

    return opportunityParams;
  }, [qsparams, showLast24HourOnly, showFullyFundedOnly, selectedCountry, selectedUniversity, selectedDiscipline, currentPage, opportunitiesPerPage, startDate, endDate]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const { data: responseData = { data: [], pagination: { total: 0 } }, isLoading: loading } = useQuery(
    ["get all opportunities", params],
    OpportunityService.getAllOpportunities,
    params,
  );
  const opportunities = responseData.data;
  const { total } = responseData.pagination || { total: 0 };
  const totalPages = Math.ceil(total / opportunitiesPerPage);

  const { data: countries = [] } = useQuery(
    ["get all countries"],
    OpportunityService.getOpportunityCountries,
    { category: params?.category },
    { enabled: Boolean(params?.category) },
  );

  const { data: disciplines = [] } = useQuery(
    ["get all disciplines"],
    OpportunityService.getOpportunityDisciplineCount,
    { category: params?.category },
    { enabled: Boolean(params?.category) },
  );

  const { data: universities = [] } = useQuery(
    ["get all universities", params?.country],
    OpportunityService.getOpportunityCountryUni,
    { category: params?.category, country: params?.country },
    { enabled: Boolean(params?.category && params?.country) },
  );

  // Set selected values based on URL parameters
  useEffect(() => {
    if (country) setSelectedCountry(replaceHyphensWithSpaces(country));
    if (university) setSelectedUniversity(replaceHyphensWithSpaces(university));
    if (discipline) setSelectedDiscipline(replaceHyphensWithSpaces(discipline));
  }, [country, university, discipline]);

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedCountry("");
    setSelectedUniversity("");
    setSelectedDiscipline("");
    setShowSavedOnly(false);
    setShowLast24HourOnly(false);
    setShowFullyFundedOnly(false);
    setQueryParams({}, { replace: true }, true); // Clear all query parameters
    navigate(`/phd-vacancies/${category}`); // Navigate to the base URL
  };

  const handleCountryChange = (event) => {
    const country = event.target.value;
    setSelectedCountry(country);
    setSelectedUniversity("");
    setShowSavedOnly(false); // Reset "Saved Selections" checkbox

    trackEvent({
      category: 'Filter',
      action: 'Select Country',
      label: country || 'None Selected'
    });

    // Construct the URL dynamically based on selected filters
    let newUrl = `/phd-vacancies/${category}`;

    if (isNewUrlStructure) {
      // New URL structure: /discipline/:discipline?/country/:country?/university/:university?
      if (selectedDiscipline) {
        newUrl += `/discipline/${replaceSpacesWithHyphens(selectedDiscipline)}`;
      }
      if (country) {
        newUrl += `/country/${replaceSpacesWithHyphens(country)}`;
      }
    } else {
      // Old URL structure: /:country?/:university?
      if (country) {
        newUrl += `/${replaceSpacesWithHyphens(country)}`;
      }
    }

    navigate(newUrl);
    // Scroll to top when filter changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUniversityChange = (event) => {
    const university = event.target.value;
    setSelectedUniversity(university);
    setShowSavedOnly(false); // Reset "Saved Selections" checkbox

    trackEvent({
      category: 'Filter',
      action: 'Select University',
      label: university || 'None Selected'
    });

    // Construct the URL dynamically based on selected filters
    let newUrl = `/phd-vacancies/${category}`;

    if (isNewUrlStructure) {
      // New URL structure: /discipline/:discipline?/country/:country?/university/:university?
      if (selectedDiscipline) {
        newUrl += `/discipline/${replaceSpacesWithHyphens(selectedDiscipline)}`;
      }
      if (selectedCountry) {
        newUrl += `/country/${replaceSpacesWithHyphens(selectedCountry)}`;
      }
      if (university) {
        newUrl += `/university/${replaceSpacesWithHyphens(university)}`;
      }
    } else {
      // Old URL structure: /:country?/:university?
      if (selectedCountry) {
        newUrl += `/${replaceSpacesWithHyphens(selectedCountry)}`;
      }
      if (university) {
        newUrl += `/${replaceSpacesWithHyphens(university)}`;
      }
    }

    navigate(newUrl);
    // Scroll to top when filter changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDisciplineChange = (event) => {
    const discipline = event.target.value;
    setSelectedDiscipline(discipline);
    setSelectedUniversity("");
    setShowSavedOnly(false); // Reset "Saved Selections" checkbox

    trackEvent({
      category: 'Filter',
      action: 'Select Discipline',
      label: discipline || 'None Selected'
    });

    // Construct the URL dynamically based on selected filters
    let newUrl = `/phd-vacancies/${category}`;

    if (isNewUrlStructure) {
      // New URL structure: /discipline/:discipline?/country/:country?/university/:university?
      if (discipline) {
        newUrl += `/discipline/${replaceSpacesWithHyphens(discipline)}`;
      }
      if (selectedCountry) {
        newUrl += `/country/${replaceSpacesWithHyphens(selectedCountry)}`;
      }
    } else {
      // Old URL structure: /:country?/:university?
      if (selectedCountry) {
        newUrl += `/${replaceSpacesWithHyphens(selectedCountry)}`;
      }
    }

    navigate(newUrl);
    // Scroll to top when filter changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const gt = searchParams.get('created_at[gt]');
    const lt = searchParams.get('created_at[lt]');

    if (gt && !lt) {
      const gtTimestamp = parseInt(gt, 10);
      const endTimestamp = gtTimestamp + 24 * 60 * 60;
      searchParams.set('created_at[lt]', endTimestamp);
      navigate(`?${searchParams.toString()}`, { replace: true });
    }
  }, [navigate]);

  // Update startDate and endDate when URL params change
  useEffect(() => {
    const gt = qsparams['created_at[gt]'];
    const lt = qsparams['created_at[lt]'];
    setStartDate(gt ? new Date(gt * 1000) : null);
    setEndDate(lt ? new Date(lt * 1000) : null);
  }, [qsparams]);

  // Handle start date change
  const handleStartDateChange = (date) => {
    const newParams = { ...qsparams };

    if (date) {
      newParams['created_at[gt]'] = Math.floor(date.getTime() / 1000);
      if (!newParams['created_at[lt]']) {
        // Set end date to now if not present
        const now = new Date();
        newParams['created_at[lt]'] = Math.floor(now.getTime() / 1000);
        setEndDate(now);
      }
    } else {
      delete newParams['created_at[gt]'];
      // Remove end date if it was auto-added
      if (newParams['created_at[lt]'] && !qsparams['created_at[lt]']) {
        delete newParams['created_at[lt]'];
        setEndDate(null);
      }
    }
    trackEvent({
      category: 'Date Filter',
      action: 'Start Date change',
      label: university || 'None Selected'
    });

    setQueryParams(newParams, { replace: true }, true);
    setStartDate(date);
    // Scroll to top when filter changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle end date change
  const handleEndDateChange = (date) => {
    const newParams = { ...qsparams };

    if (date) {
      newParams['created_at[lt]'] = Math.floor(date.getTime() / 1000);
    } else {
      delete newParams['created_at[lt]'];
    }

    trackEvent({
      category: 'Date Filter',
      action: 'End Date change',
      label: university || 'None Selected'
    });

    setQueryParams(newParams, { replace: true }, true);
    setEndDate(date);
    // Scroll to top when filter changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the click is outside both the sidebar and any select elements
      const isSelectElement = event.target.tagName.toLowerCase() === 'select';
      const isSelectOption = event.target.tagName.toLowerCase() === 'option';

      // If the click is on a select or option element, don't close the sidebar
      if (isSelectElement || isSelectOption) {
        return;
      }

      // Ignore clicks on elements with data-ignore-outside-click attribute
      if (event.target.closest('[data-ignore-outside-click]')) {
        return;
      }

      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarOpen(false);
      }
    }

    // Only add the event listener if the sidebar is open
    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarOpen]);

  const filteredOpportunities = opportunities.filter(
    (opportunity) =>
      (!showSavedOnly || finalSavedOpportunities?.has(opportunity.id))
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCountry, selectedUniversity, selectedDiscipline, showLast24HourOnly, showFullyFundedOnly]);

  const currentOpportunities = filteredOpportunities;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    trackEvent({
      category: 'Pagination',
      action: 'Change Page',
      label: `Page ${newPage}`
    });
    window.scrollTo({ top: 10, behavior: "smooth" });
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCheckboxChange = (setter, label) => {
    setter((prev) => !prev);
    trackEvent({
      category: 'Filter',
      action: 'Toggle Checkbox',
      label: label
    });

    // Update URL query parameters for the Fully Funded checkbox
    if (label === 'Fully Funded') {
      const newValue = !showFullyFundedOnly;
      if (newValue) {
        setQueryParams({ ...qsparams, funded: true }, { replace: true }, true);
      } else {
        const { funded: _, ...newParams } = qsparams; // Remove the funded parameter
        setQueryParams(newParams, { replace: true }, true);
      }
    }
    
    // Scroll to top when filter changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleSave = (opportunityId) => {
    if (!user) {
      return; // Redirect to login or handle user not logged in
    }

    setSavedOpportunities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(opportunityId)) {
        newSet.delete(opportunityId);
      } else {
        newSet.add(opportunityId);
      }
      return newSet;
    });
  };

  const {
    data: getSavedOpportunities = [],
    error: savedOppError,
    isLoading: loadingSavedOpp,
    isSuccess: savedOppSuccess,
  } = useQuery(
    ["getSavedOpportunities", user?.id],
    () => OpportunityService.getSavedOpportunities({ userId: user.id }),
    {
      enabled: !!user,
    }
  );

  useEffect(() => {
    const opportunitiesArray = Array.isArray(getSavedOpportunities?.savedOpportunities)
      ? getSavedOpportunities.savedOpportunities
      : [];
    if (savedOppSuccess && opportunitiesArray.length > 0) {
      setSavedOpportunities(new Set(opportunitiesArray));
    }
  }, [getSavedOpportunities, savedOppSuccess]);

  const { mutateAsync: createSaveOpportunity } = useMutations(
    OpportunityService.createSavedOpportunity
  );

  const { mutateAsync: deleteSaveOpportunity } = useMutations(
    OpportunityService.deleteSavedOpportunities
  );

  const handleToggleSave = async (opportunityId) => {
    if (!user) {
      return; // Redirect to login or handle user not logged in
    }

    try {
      if (finalSavedOpportunities.has(opportunityId)) {
        await deleteSaveOpportunity({
          user_id: user.id,
          opportunity_id: opportunityId,
        });
        toggleSave(opportunityId);
        trackEvent({
          category: 'Opportunity',
          action: 'UnSave',
          label: opportunityId
        });
      } else {
        await createSaveOpportunity({
          user_id: user.id,
          opportunity_id: opportunityId,
        });
        toggleSave(opportunityId);
        trackEvent({
          category: 'Opportunity',
          action: 'Save',
          label: opportunityId
        });
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  // Effect to synchronize checkbox state with URL parameter
  useEffect(() => {
    const createdAtParam = qsparams['created_at[gt]'];
    if (createdAtParam) {
      setShowLast24HourOnly(true);
    } else {
      setShowLast24HourOnly(false);
    }

    const fundedParam = qsparams['funded'];
    if (fundedParam) {
      setShowFullyFundedOnly(true);
    } else {
      setShowFullyFundedOnly(false);
    }
  }, [qsparams]);

  const handleLast24HourChange = () => {
    const newValue = !showLast24HourOnly; // Determine the new value
    setShowLast24HourOnly(newValue); // Update the state

    // Update the URL parameters based on the checkbox state
    if (newValue) {
      const timestamp = getLast24HoursTimestamp();
      setQueryParams({ ...qsparams, 'created_at[gt]': timestamp }, { replace: true }, true);
    } else {
      // If the checkbox is unchecked, remove the created_at[gt] parameter from the URL
      const { 'created_at[gt]': _, ...newParams } = qsparams; // Remove the created_at[gt] parameter
      setQueryParams(newParams, { replace: true }, true);
    }

    // Track the checkbox change event
    trackEvent({
      category: 'Filter',
      action: 'Toggle Checkbox',
      label: 'Published in Last 24 Hours',
      value: newValue ? 'Enabled' : 'Disabled'
    });
    
    // Scroll to top when filter changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSocialShare = (platform) => {
    trackEvent({
      category: 'Social',
      action: 'Share',
      label: platform
    });
  };

  useEffect(() => {
    // Scroll to the top of the page when component mounts
    window.scrollTo(0, 10);
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      <WetsusBanner />
      <SEOMetadata
        title={`Find Your Ideal PhD Position${effectiveDiscipline ? ` in ${effectiveDiscipline}` : effectiveUniversity ? ` at ${effectiveUniversity}` : ''}${effectiveCountry ? ` in ${effectiveCountry}` : ''}${total > 0 ? ` - ${total} Vacancies Available` : ''} | PhDScanner`}
        description={`Discover ${total > 0 ? `${total} ` : ''}${effectiveDiscipline ? `${effectiveDiscipline} ` : effectiveUniversity ? `${effectiveUniversity} ` : ''}PhD positions${effectiveCountry ? ` in ${effectiveCountry}` : ''}. Explore doctoral programs, fully funded opportunities${effectiveDiscipline ? ` in ${effectiveDiscipline}` : effectiveUniversity ? ` at ${effectiveUniversity}` : ''}${effectiveCountry ? ` across top universities in ${effectiveCountry}` : ''}. Start your academic journey today with PhDScanner.`}
        keywords={`
    ${effectiveCountry ? `
      PhD in ${effectiveCountry},
      ${effectiveCountry} PhD,
      fully funded PhD ${effectiveCountry},
      ${effectiveCountry} PhD vacancies,
      PhD openings in ${effectiveCountry},
      PhD positions ${effectiveCountry} ${new Date().getFullYear()},
      PhD vacancies ${effectiveCountry} ${new Date().getFullYear()},
      PhD grants ${effectiveCountry},
      PhD scholarships ${effectiveCountry},
      international PhD ${effectiveCountry},
      research fellowships ${effectiveCountry},
    ` : ''}
    ${effectiveUniversity ? `
      PhD in ${effectiveUniversity},
      ${effectiveUniversity} PhD,
      fully funded PhD ${effectiveUniversity},
      ${effectiveUniversity} PhD vacancies,
      PhD openings in ${effectiveUniversity},
      PhD positions ${effectiveUniversity} ${new Date().getFullYear()},
      PhD vacancies ${effectiveUniversity} ${new Date().getFullYear()},
      PhD grants ${effectiveUniversity},
      PhD scholarships ${effectiveUniversity},
      international PhD ${effectiveUniversity},
      research fellowships ${effectiveUniversity},
    ` : ''}
    Scholarship Abroad,
    PhD positions${effectiveDiscipline ? ` in ${effectiveDiscipline}` : ''}${effectiveCountry ? ` in ${effectiveCountry}` : ''}${effectiveUniversity ? ` at ${effectiveUniversity}` : ''},
    ${effectiveDiscipline || effectiveUniversity || ''} PhD vacancies,
    doctoral programs ${effectiveDiscipline ? `in ${effectiveDiscipline}` : effectiveCountry ? `in ${effectiveCountry}` : ''},
    fully funded PhD${effectiveDiscipline ? ` in ${effectiveDiscipline}` : effectiveUniversity ? ` at ${effectiveUniversity}` : ''},
    ${effectiveDiscipline || effectiveCountry || ''} universities PhD opportunities,
    ${effectiveDiscipline || effectiveUniversity || ''} PhD scholarships,
    research positions${effectiveDiscipline ? ` in ${effectiveDiscipline}` : effectiveCountry ? ` in ${effectiveCountry}` : ''}
  `.replace(/\s+/g, ' ').trim()}
        imageUrl="https://www.phdscanner.com/phdscanner_logo.webp"
        url={`https://www.phdscanner.com/phd-vacancies/${effectiveCategory}${effectiveDiscipline ? `/discipline/${replaceSpacesWithHyphens(effectiveDiscipline)}` : ''}${country ? `/${replaceSpacesWithHyphens(country)}` : ''}${university ? `/${replaceSpacesWithHyphens(university)}` : ''}`}
      />

      <OpportunityLayout
        sidebarRef={sidebarRef}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        filtersContent={
          <OpportunityFilters
            selectedCountry={selectedCountry}
            selectedUniversity={selectedUniversity}
            selectedDiscipline={selectedDiscipline}
            startDate={startDate}
            endDate={endDate}
            showFullyFundedOnly={showFullyFundedOnly}
            countries={countries}
            universities={universities}
            disciplines={disciplines}
            handleCountryChange={handleCountryChange}
            handleUniversityChange={handleUniversityChange}
            handleDisciplineChange={handleDisciplineChange}
            handleStartDateChange={handleStartDateChange}
            handleEndDateChange={handleEndDateChange}
            handleCheckboxChange={handleCheckboxChange}
            handleClearFilters={handleClearFilters}
            setIsSidebarOpen={setIsSidebarOpen}
            setShowFullyFundedOnly={setShowFullyFundedOnly}
          />
        }
      >
        {/* Page Title Section */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-lg">
                  <FaGraduationCap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                    {category === "featured" ? "🌟 Featured Opportunities" :
                      `${total > 0 ? total : ''} PhD Positions${discipline ? ` in ${replaceHyphensWithSpaces(discipline)}` : ''}${country ? ` in ${replaceHyphensWithSpaces(country)}` : ''}${university ? ` at ${replaceHyphensWithSpaces(university)}` : ''}`}
                  </h1>
                  <p className="text-xs text-gray-600 mt-1">
                    Find your perfect research opportunity
                  </p>
                </div>
              </div>

              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden inline-flex items-center px-3 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <FaFilter className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Filters</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 pb-12 w-full">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#DB4F40] border-t-transparent" />
            </div>
          ) : opportunities.length > 0 ? (
            <div className="space-y-6">
              {/* Opportunities Grid */}
              <Suspense fallback={
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#DB4F40] border-t-transparent" />
                </div>
              }>
                <OpportunityGrid
                  opportunities={opportunities}
                  user={user}
                  savedOpportunities={finalSavedOpportunities}
                  handleToggleSave={handleToggleSave}
                />
              </Suspense>

              {/* Modern Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4 py-8">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-[#DB4F40] text-gray-700 hover:text-[#DB4F40] hover:bg-[#FEF2F2] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>
                  <div className="flex items-center space-x-2">
                    <span className="px-4 py-2 bg-[#DB4F40] text-white rounded-lg font-medium">
                      Page {currentPage} of {totalPages}
                    </span>
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-[#DB4F40] text-gray-700 hover:text-[#DB4F40] hover:bg-[#FEF2F2] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Next
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Follow Us On Section */}
              <div className="w-full mt-8">
                <div className="w-full bg-gradient-to-b from-[#FFEEED] to-[#FFD6D4] border-2 border-[#DB4F40]/20 rounded-2xl shadow-lg p-6 md:p-8 flex flex-col items-center">
                  <span className="text-[#DB4F40] text-xl md:text-2xl font-bold mb-1 tracking-wide text-center">
                    Follow us on
                  </span>
                  <span className="text-[#1B222A]/80 text-base md:text-lg mb-6 text-center font-medium">
                    Join our growing community!
                  </span>
                  <div className="flex flex-wrap justify-center gap-8 w-full">
                    {/* LinkedIn */}
                    <a
                      href="https://www.linkedin.com/company/phdscanner"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="group flex flex-col items-center justify-center gap-2 p-4 bg-white border-2 border-[#0077b5]/20 rounded-xl shadow-md hover:shadow-xl hover:border-[#0077b5] transition-all duration-200 min-w-[90px] cursor-pointer hover:scale-105 active:scale-95"
                      onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'LinkedIn' })}
                    >
                      <i className="fab fa-linkedin text-[#0077b5] text-3xl md:text-4xl group-hover:animate-bounce"><svg className="inline-block align-middle" width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm15.5 11.268h-3v-5.604c0-1.337-.025-3.063-1.868-3.063-1.868 0-2.154 1.459-2.154 2.967v5.7h-3v-10h2.881v1.367h.041c.401-.761 1.379-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.601v5.595z"/></svg></i>
                      <span className="text-[#1B222A] font-bold text-lg md:text-xl leading-none">27k+</span>
                      <span className="text-xs text-[#1B222A]/70 font-medium">Followers</span>
                    </a>
                    {/* Twitter */}
                    <a
                      href="https://x.com/PhdScanner"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Twitter"
                      className="group flex flex-col items-center justify-center gap-2 p-4 bg-white border-2 border-[#1da1f2]/20 rounded-xl shadow-md hover:shadow-xl hover:border-[#1da1f2] transition-all duration-200 min-w-[90px] cursor-pointer hover:scale-105 active:scale-95"
                      onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'Twitter' })}
                    >
                      <i className="fab fa-twitter text-[#1da1f2] text-3xl md:text-4xl group-hover:animate-bounce"><svg className="inline-block align-middle" width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-2.719 0-4.924 2.206-4.924 4.924 0 .386.044.763.127 1.124-4.09-.205-7.719-2.165-10.148-5.144-.424.729-.666 1.577-.666 2.476 0 1.708.87 3.216 2.188 4.099-.807-.026-1.566-.247-2.228-.616v.062c0 2.385 1.697 4.374 3.946 4.827-.413.112-.849.171-1.298.171-.317 0-.626-.03-.928-.086.627 1.956 2.444 3.377 4.6 3.417-1.68 1.318-3.808 2.105-6.115 2.105-.398 0-.79-.023-1.175-.069 2.179 1.397 4.768 2.213 7.557 2.213 9.054 0 14.009-7.496 14.009-13.986 0-.213-.005-.425-.014-.636.962-.695 1.797-1.562 2.457-2.549z"/></svg></i>
                      <span className="text-[#1B222A] font-bold text-lg md:text-xl leading-none">2k+</span>
                      <span className="text-xs text-[#1B222A]/70 font-medium">Followers</span>
                    </a>
                    {/* Facebook */}
                    <a
                      href="https://www.facebook.com/Phdscanner"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="group flex flex-col items-center justify-center gap-2 p-4 bg-white border-2 border-[#1877f3]/20 rounded-xl shadow-md hover:shadow-xl hover:border-[#1877f3] transition-all duration-200 min-w-[90px] cursor-pointer hover:scale-105 active:scale-95"
                      onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'Facebook' })}
                    >
                      <i className="fab fa-facebook text-[#1877f3] text-3xl md:text-4xl group-hover:animate-bounce"><svg className="inline-block align-middle" width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M22.675 0h-21.35c-.733 0-1.325.592-1.325 1.326v21.348c0 .733.592 1.326 1.325 1.326h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.312h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.326v-21.349c0-.734-.593-1.326-1.324-1.326z"/></svg></i>
                      <span className="text-[#1B222A] font-bold text-lg md:text-xl leading-none">1k+</span>
                      <span className="text-xs text-[#1B222A]/70 font-medium">Followers</span>
                    </a>
                    {/* Instagram */}
                    <a
                      href="https://www.instagram.com/phdscanner"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="group flex flex-col items-center justify-center gap-2 p-4 bg-white border-2 border-[#e4405f]/20 rounded-xl shadow-md hover:shadow-xl hover:border-[#e4405f] transition-all duration-200 min-w-[90px] cursor-pointer hover:scale-105 active:scale-95"
                      onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'Instagram' })}
                    >
                      <FaInstagram className="text-[#e4405f] text-3xl md:text-4xl group-hover:animate-bounce" />
                      <span className="text-[#1B222A] font-bold text-lg md:text-xl leading-none">1k+</span>
                      <span className="text-xs text-[#1B222A]/70 font-medium">Followers</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-[#FEF2F2] rounded-full flex items-center justify-center mx-auto mb-4">
                <FaGraduationCap className="w-8 h-8 text-[#DB4F40]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No opportunities found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your filters or check back later</p>
              <button
                onClick={handleClearFilters}
                className="inline-flex items-center px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-colors"
              >
                <FaFilter className="w-4 h-4 mr-2" />
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </OpportunityLayout>
    </div>
  );
};